import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, Monitor, Smartphone, Tablet, Plus, CheckCircle } from 'lucide-react';

/**
 * Modal for selecting assessment variants
 * Shows when assessment has many variants (>3) for better UX
 */
const VariantSelectorModal = ({ isOpen, onClose, assessment, onSelectVariant, onCreateVariant }) => {
  if (!isOpen || !assessment) return null;

  const isPrimary = assessment.variants && assessment.variants.length > 0;
  const variants = assessment.variants || [];

  const getDeviceIcon = (deviceType) => {
    const icons = {
      phone: Smartphone,
      tablet: Tablet,
      desktop: Monitor,
      other: Globe
    };
    return icons[deviceType] || Globe;
  };

  // Status chip tone (FECOA-2174)
  const getStatusColor = (status) => {
    switch(status) {
      case 'published':
        return 'badge-success';
      case 'review':
        return 'badge-info';
      case 'draft':
        return 'badge-warning';
      default:
        return 'badge-gray';
    }
  };

  const cardClass = 'relative rounded-xl border border-gray-200/80 bg-white p-4 text-left shadow-card transition-colors hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Select Version</h2>
              <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="btn btn-ghost btn-sm btn-icon -mr-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {/* Primary Version */}
              {isPrimary && (
                <motion.button
                  onClick={() => {
                    onSelectVariant(assessment);
                    onClose();
                  }}
                  className={cardClass}
                >
                  <div className="absolute right-3 top-3">
                    <span className="badge badge-brand">
                      PRIMARY
                    </span>
                  </div>

                  <div className="mb-3 mt-6 space-y-2">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      <span className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                        {assessment.language || 'en-US'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Monitor className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      <span className="text-[13px] text-gray-500 dark:text-gray-400">Web Desktop (Primary)</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                    <span className="code-chip">v{assessment.version || '1.0'}</span>
                    <CheckCircle className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                </motion.button>
              )}

              {/* Variant Versions */}
              {variants.map((variant) => {
                const DeviceIcon = getDeviceIcon(variant.device_type);
                const statusColor = getStatusColor(variant.publication_status);

                return (
                  <motion.button
                    key={variant.id}
                    onClick={() => {
                      onSelectVariant(variant);
                      onClose();
                    }}
                    className={cardClass}
                  >
                    <div className="absolute right-3 top-3">
                      <span className="badge badge-gray">
                        VARIANT
                      </span>
                    </div>

                    <div className="mb-3 mt-6 space-y-2">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                          {variant.language}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DeviceIcon className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="text-[13px] text-gray-500 dark:text-gray-400">{variant.device_name}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                      <span className="code-chip">v{variant.version || '1.0'}</span>
                      <span className={`badge badge-dot capitalize ${statusColor}`}>{variant.publication_status}</span>
                    </div>
                  </motion.button>
                );
              })}

              {/* Create New Variant */}
              {isPrimary && (
                <motion.button
                  onClick={() => {
                    onCreateVariant(assessment);
                    onClose();
                  }}
                  className="flex min-h-[140px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 p-4 transition-colors hover:border-primary-500 hover:bg-primary-50/40 dark:border-white/15 dark:hover:border-primary-400 dark:hover:bg-primary-400/10"
                >
                  <Plus className="mb-1.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <span className="text-[13.5px] font-medium text-gray-900 dark:text-gray-50">Create New Variant</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Add language or device version</span>
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default VariantSelectorModal;
